import { useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Download, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * UserImportDialog — bulk-import users from CSV. Reuses invite-email pipeline.
 */
export function UserImportDialog({ open, onOpenChange, onImported }) {
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const templateHref = `${API}/admin/users/import-template.csv`;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/users/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { send_invites: sendInvites },
      });
      setResult(data);
      if (data.errors?.length) {
        toast.warning(`${data.created} imported · ${data.skipped_existing} existed · ${data.errors.length} rejected`);
      } else {
        toast.success(`${data.created} user${data.created === 1 ? "" : "s"} imported${data.skipped_existing ? ` · ${data.skipped_existing} already existed` : ""}`);
      }
      onImported?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setResult(null); onOpenChange(v); }}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="user-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4 text-orange-400" /> Bulk import users</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Drop a CSV with the columns <code className="text-orange-300">name,email,role,property_ids,unit,phone</code>.
            Property IDs are semicolon-separated so Excel doesn&apos;t split them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <a
            href={templateHref}
            className="inline-flex items-center gap-1 text-xs text-orange-300 hover:text-orange-200"
            data-testid="user-import-template"
          >
            <Download className="h-3 w-3" /> Download CSV template
          </a>

          <div className="flex items-center justify-between rounded-md border border-white/10 px-3 py-2">
            <div className="text-xs">
              <Label className="text-zinc-200 cursor-pointer">Email password-reset invites</Label>
              <div className="text-[11px] text-zinc-500">Every new user gets a set-your-password link (14-day expiry).</div>
            </div>
            <Switch checked={sendInvites} onCheckedChange={setSendInvites} data-testid="user-import-invite-toggle" />
          </div>

          <input ref={fileRef} type="file" hidden accept=".csv"
                 onChange={(e) => upload(e.target.files?.[0])}
                 data-testid="user-import-file-input" />

          <Button onClick={() => fileRef.current?.click()} disabled={busy}
                  className="w-full bg-orange-500 hover:bg-orange-600 rounded-full"
                  data-testid="user-import-pick-file">
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Choose CSV & import"}
          </Button>

          {result && (
            <div className="rounded-md border border-white/10 p-3 text-xs" data-testid="user-import-result">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Created</div>
                     <div className="text-lg text-emerald-300 tabular-nums" data-testid="user-import-created">{result.created}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Skipped</div>
                     <div className="text-lg text-zinc-400 tabular-nums" data-testid="user-import-skipped">{result.skipped_existing}</div></div>
                <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Errors</div>
                     <div className="text-lg text-red-300 tabular-nums" data-testid="user-import-errors">{result.errors?.length || 0}</div></div>
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-[11px] text-red-300">Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UserImportDialog;
